import { ulEdit05 } from '@faceup/icons/ulEdit05'
import { ulFileSearch02 } from '@faceup/icons/ulFileSearch02'
import { ulHomeLine } from '@faceup/icons/ulHomeLine'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { useMatchPath, useNavigate } from '@faceup/router'
import { Institution } from '@faceup/utils'
import { type FragmentType, getFragmentData, graphql } from '../../../../__generated__'
import { ButtonItem, MobileFooterShell } from '../../../../Components/Footer/MobileFooterShell'
import { useIsCheckReportAvailable } from '../../../../hooks/useIsCheckReportAvailable'
import { getHomepageRouteCallback } from '../../../../utils/urls'

const messages = defineMessages('followUp.components.institutionLayout.components.appFooter', [
  { id: 'createReport', defaultMessage: 'Create a case' },
  { id: 'checkReport', defaultMessage: 'Check a case' },
  { id: 'createReportForTestingSchool', defaultMessage: 'Create test case' },
  { id: 'home', defaultMessage: 'Home' },
] as const)

const fragments = {
  AppFooter_reportSource: graphql(`
    fragment AppFooter_reportSource on PublicReportSource {
      id
      pages(language: $language) {
        ... on Page {
          id
        }
      }
      institution {
        id
        isTesting
        type
        billing {
          id
          plan
        }
      }
    }
  `),
}

type AppFooterProps = {
  reportSource: FragmentType<typeof fragments.AppFooter_reportSource>
}
export const AppFooter = ({ reportSource: _reportSource }: AppFooterProps) => {
  const reportSource = getFragmentData(fragments.AppFooter_reportSource, _reportSource)
  const { isCheckReportAvailable } = useIsCheckReportAvailable({
    plan: reportSource.institution?.billing?.plan,
  })
  const matchPath = useMatchPath()
  const navigate = useNavigate()
  const homepage = getHomepageRouteCallback(reportSource.pages)

  const isTestingSchool =
    reportSource.institution.isTesting === true &&
    reportSource.institution.type === Institution.School

  // Home and create are always available inside the branch; check is plan-gated.
  const visibleMenuItems = [true, true, isCheckReportAvailable]

  const countMenuItems = visibleMenuItems.filter(Boolean).length

  return (
    <MobileFooterShell itemCount={countMenuItems}>
      <ButtonItem
        onClick={() => navigate(homepage)}
        active={Boolean(
          matchPath(routes => routes.page(), {
            wildcard: true,
          })
        )}
        icon={ulHomeLine}
      >
        <FormattedMessage message={messages.home} />
      </ButtonItem>
      <ButtonItem
        onClick={() => navigate(routes => routes.createReport())}
        active={
          Boolean(matchPath(routes => routes.createReport())) ||
          Boolean(matchPath(routes => routes.sentReport()))
        }
        icon={ulEdit05}
      >
        {isTestingSchool ? (
          <FormattedMessage message={messages.createReportForTestingSchool} />
        ) : (
          <FormattedMessage message={messages.createReport} />
        )}
      </ButtonItem>
      {isCheckReportAvailable && (
        <ButtonItem
          onClick={() => navigate(routes => routes.checkReport())}
          active={
            Boolean(matchPath(routes => routes.report())) ||
            Boolean(matchPath(routes => routes.checkReport(), { wildcard: true }))
          }
          icon={ulFileSearch02}
        >
          <FormattedMessage message={messages.checkReport} />
        </ButtonItem>
      )}
    </MobileFooterShell>
  )
}
